<template>
  <div class="page_participants">
    <h1>Účastníci</h1>
    <a
      class="exchange"
      href="https://docs.google.com/spreadsheets/d/1f24SkcTo8g0VRJgLbDwXo0PzCdIHkL10TaiyfYMXIgM/edit?usp=sharing"
      target="_blank">
      <i class="fas fa-exchange-alt"></i> Burza účastníků
    </a>
    <div class="actions">
      <button class="add" @click="add" title="Přidat účastníka">
        <i class="fas fa-plus"></i>
      </button>
      <a class="importInfo" title="Import více dat" href="https://docs.google.com/document/d/1n6S7fpr6OSya595Lk_GaT0GMnQPzD14PiUNYjukW54M/edit#heading=h.9gq9jfi8r87" target="_blank">
        <i class="fas fa-plus"></i><i class="fas fa-plus"></i>
      </a>
      <button class="export" title="Exportovat seznam" @click="exportAll">
        <i class="fa fa-file-export"></i>
      </button>
    </div>
    <div class="registration-end">
      Možnost změny týmu a Rangera roku končí:
      <strong>{{ config.teamAndRoyRegistrationEndAt | cz }}</strong>
    </div>
    <tabloid name="participants" :columns="columns" :data="participants">
      <template v-slot:name="{ row }">
        {{ row.borowedPlayer }}
        <inputer name="Jméno"
          :paths="['participants', row.id, 'name']"
          :onMultiInsert="lines => multiInsert(row, lines)"
          :disabled="row.isDisabled"
          :required="true" />
      </template>
      <template v-slot:surname="{ row }">
        <inputer
          name="Příjmení"
          :paths="['participants', row.id, 'surname']"
          :disabled="row.isDisabled"
          :required="true"
        />
      </template>
      <template v-slot:birthDate="{ row }">
        <date-inputer
          name="Datum narození"
          :paths="['participants', row.id, 'birthDate']"
          :disabled="row.isDisabled"
          :required="true"
        />
      </template>
      <template v-slot:categoryId="{ value }">
        {{ value | cz }}
      </template>
      <template v-slot:teamId="{ row }">
        <select-inputer
          :paths="['participants', row.id, 'teamId']"
          :items="row.available_teams"
          :disabled="row.isDisabled || isTeamChangeNotAllowed"
          item_null="true"
        >
          <template v-slot:default="props">{{ props.id != null ? teams[props.id] : props.label | team_name_full }}</template>
          <template v-slot:item="props">{{ props.item | team_name_full }}</template>
        </select-inputer>
      </template>
      <template v-slot:larpTeamId="{ value }">
        {{ value | larp_team_name_short }}
      </template>
      <template v-slot:isRoy="{ row }">
        <progress-check-inputer
          v-if="row.hasRoyCategory"
          :paths="['participants', row.id, 'isRoy']"
          :label="`${row.royCount}/3`"
          :disabled="row.isDisabled || isTeamChangeNotAllowed"
          :checkedWidth="0.3"
          :allWidth="row.royCount/3"
          :color="row.royColor"
        />
        <div v-else></div>
      </template>
      <template v-slot:isFree="{ row }">
        <check-inputer
          :paths="['participants', row.id, 'isFree']"
          :disabled="row.isDisabled || row.teamId != null"
        />
      </template>
      <template v-slot:partilyDays="{ row }">
        <dropdownCheckInputer
          :paths="['participants', row.id, 'partilyDays']"
          :items="partilyDays"
          :disabled="row.isDisabled || row.isServant || row.playingOutpostId != row.financialOutpostId"
        >
          <template v-slot:item="props">{{ props.item | participant_partilyDays }}</template>
        </dropdownCheckInputer>
      </template>
      <template v-slot:isServant="{ row }">
        <check-inputer
          :paths="['participants', row.id, 'isServant']"
          :disabled="row.isDisabled || row.isServantConfirmed"
        />
        <i
          v-if="row.isServant"
          class="fa"
          :class="{ 'fa-check': row.isServantConfirmed, 'fa-hourglass-half': !row.isServantConfirmed }"
          :title="row.isServantConfirmed ? 'Rozhodčí schválen' : 'Čeká na schválení'"
        />
      </template>
      <template v-slot:healthProblems="{ row }">
        <dropdownCheckInputer
          :paths="['participants', row.id, 'healthProblems']"
          :items="healthProblems"
          :disabled="row.isDisabled"
        >
          <template v-slot:label="{ items }"><template v-for="item in items">{{ item | cz_healthProblemShort }} </template></template>
          <template v-slot:item="{ item }">{{ item | cz_healthProblemLong }}</template>
        </dropdownCheckInputer>
      </template>
      <template v-slot:healthProblemsDescription="{ row }">
        <inputer
          :paths="['participants', row.id, 'healthProblemsDescription']"
          :disabled="row.isDisabled" />
      </template>
      <template v-slot:actions="{ row }">
        <selectInputer
          :paths="[ 'participants', row.id, 'playingOutpostId' ]"
          :items="outposts"
          :getKey="(item, key) => Number(key)"
          :disabled="row.isDisabled"
          confirmation="Opravdu chcete předat účastníka do jiné hlídky? Účastník pojede za jinou hlídku, ale vaše hlídka za něj bude platit"
        >
          <template v-slot:default>
            <span
              v-if="row.isDisabled"
              title="účastník předán hlídce"
            >{{ row.playingOutpostId }}. PH</span>
            <i
              v-else
              class="fa fa-share"
              title="Předat jiné hlídce (účastník jede za jinou hlídku, naše hlídka za něj bude platit)"
            ></i>
          </template>
          <template v-slot:item="props">{{ props.id }}. PH - {{ props.item }}</template>
        </selectInputer>
        <button class="remove" @click="remove(row)" title="Odebrat účastníka">
          <i class="fa fa-times"></i>
        </button>
      </template>
      <template v-slot:secondRow="{ row }">
        <td data-holder="servantType" colspan="2">
          <selectInputer
            :paths="[ 'participants', row.id, 'servantType' ]"
            :items="servantTypes"
            :disabled="row.isDisabled"
          >
            <template v-slot:default="props">{{ props.label | cz }}</template>
            <template v-slot:item="props">{{ props.item | cz }}</template>
          </selectInputer>
        </td>
        <td data-holder="servantEmail" colspan="3">
          <inputer
            name="E-mail"
            :paths="['participants', row.id, 'email']"
            :disabled="row.isDisabled"
          />
        </td>
        <td data-holder="servantPhone" colspan="3">
          <inputer
            name="Telefon"
            :paths="['participants', row.id, 'phone']"
            :disabled="row.isDisabled"
          />
        </td>
        <td></td>
      </template>
    </tabloid>
  </div>
</template>

<script>
import Vue from "vue";
import { mapState } from "vuex";
import { _ } from "../extend";
import { datetime, data } from "../store_utils";
import { price } from "../rr_utils";
import tabloid from "../components/tabloid";
import inputer from "../components/inputer.vue";
import dateInputer from "../components/inputer-date";
import progressCheckInputer from "../components/inputer-check-progress";
import checkInputer from "../components/inputer-check";
import selectInputer from "../components/inputer-select";
import dropdownCheckInputer from "../components/inputer-dropdown-check";

export default {
  name: "participants",
  components: {
    tabloid,
    inputer,
    dateInputer,
    checkInputer,
    progressCheckInputer,
    selectInputer,
    dropdownCheckInputer
  },
  data: () => ({
    sortedBy: "id",
    sortedAsc: 1,
    focused: null,
    columns: {
      name: { title: "Jméno", getCellAttributes: row => row.isServant ? { rowspan: 2 } : {} },
      surname: { title: "Příjmení", getCellAttributes: row => row.isServant ? { rowspan: 2 } : {} },
      birthDate: { title: "Datum narození" },
      categoryId: { title: "Kategorie" },
      teamId: { title: "Tým na ringo" },
      larpTeamId: { title: "Tým na larp" },
      isRoy: { title: "Ranger roku", headerAttributes: { title: 'Účastní se soutěže Ranger roku?' } },
      isFree: { title: "Volný hráč", headerAttributes: { title: "Mohou ostatní hlídky zvát tohoto hráče do svého týmu?" } },
      partilyDays: { title: "Částečná účast", headerAttributes: { title: "Jede pouze na část RangerCampu? (den začíná večeří a končí odpolední svačinou kromě neděle)" } },
      isServant: { title: "Je rozhodčí", headerAttributes: { title: "Jede na RangerCamp jako rozhodčí? (musí být schváleno organizátory)" } },
      healthProblems: { title: "Zdravotní omezení" },
      healthProblemsDescription: { title: "Popis zdravotních omezení" },
      actions: { title: " " },
    }
  }),
  computed: {
    ...mapState(["config", "outposts", "partilyDays", "healthProblems", "servantTypes", "teams"]),
    isTeamChangeNotAllowed() {
      return this.config.teamAndRoyRegistrationEndAt < new Date();
    },
    participants() {
      let result = _.filter(this.$store.state.participants, participant =>
        participant.playingOutpostId == this.$store.state.outpost.id ||
        participant.financialOutpostId == this.$store.state.outpost.id
      );

      result = _.select(result, p => {
        const participant = { ...p };
        participant.borowedPlayer = p.financialOutpostId != this.$store.state.outpost.id
          ? `${p.financialOutpostId}. PH: `
          : null;
        participant.hasRoyCategory = this.$store.state.royCategories[p.categoryId] != null;
        participant.royCount = participant.hasRoyCategory
          ? Object.keys(this.$store.getters.roy[this.$store.state.royCategories[p.categoryId]]).length
          : 0;
        participant.royColor = participant.royCount == 0 || participant.royCount == 3
          ? "green"
          : participant.royCount > 3
            ? "red"
            : "yellow";
        participant.available_teams = _.filter(
          this.teams,
          (team, id) =>
            this.$store.state.childCategories[id.split("-")[0]].includes(p.categoryId)
            && team.outpostId == this.$store.state.outpost.id);
        participant.isDisabled = p.playingOutpostId != this.$store.state.outpost.id;
        participant.secondRow = p.isServant;
        participant.rowAttributes = { class: { disabled: participant.isDisabled } };
        participant.secondRowAttributes = participant.rowAttributes;
        return participant;
      });

      return result;
    }
  },
  methods: {
    add() {
      this.$store.dispatch("createParticipant");
    },
    exportAll() {
      this.$store.dispatch("exportAll");
    },
    multiInsert(participant, lines) {
      var props = ["name", "surname", "birthDate"];

      // first - update
      var propValues = lines[0].split("\t");
      for (
        let iProp = 0;
        iProp < propValues.length && iProp < props.length;
        iProp++
      ) {
        var value = propValues[iProp];
        // date value
        if (iProp == 2) {
          value = datetime.czechStringToDate(propValues[iProp]);
          this.$store.dispatch("setParticipantCategory", {
            id: participant.id,
            birthDate: datetime.dateToString(value)
          });
        }

        this.$store.commit("update", {
          paths: ["participants", participant.id, props[iProp]],
          value: value
        });
      }

      // next - add
      for (let iItem = 1; iItem < lines.length; iItem++) {
        propValues = lines[iItem].split("\t");

        var item = {};
        for (
          let iProp = 0;
          iProp < propValues.length && iProp < props.length;
          iProp++
        ) {
          // date value
          value =
            iProp == 2
              ? datetime.czechStringToDate(propValues[iProp])
              : propValues[iProp];

          Vue.set(item, props[iProp], value);
        }

        this.$store.dispatch("createParticipant", item);
      }
    },

    remove(participant) {
      if (
        participant.playingOutpostId != participant.financialOutpostId
      ) {
        this.$store.commit("notify", {
          text:
            "Účastníka můžete odstranit pouze pokud je v hlídce, která ho platí!",
          level: "warning"
        });
        return;
      }
      if (participant.isServantConfirmed) {
        this.$store.commit("notify", {
          text:
            "Nemůžete odstranit rozhodčího. Domluvte se prosím s organizátory RangerCampu.",
          level: "warning"
        });
        return;
      }
      if (!confirm("Opravdu chcete odebrat účastníka?")) return;

      // add penalty
      if (
        this.$store.state.original_values.participants == null ||
        this.$store.state.original_values.participants[participant.id] !== null
      ) {
        var destroyPrice = price.participantDestroyed(this.$store, participant);
        if (destroyPrice > 0) {
          this.$store.commit("add", {
            paths: ["priceModifiers", data.newGuid()],
            value: {
              message: `Odhlášení účastníka ${participant.name} ${participant.surname} v době ${this.$store.getters.currentAge.name}`,
              amount: destroyPrice,
              count: 1
            }
          });
        }
      }

      // remove participant
      this.$store.commit("remove", {
        paths: ["participants", participant.id]
      });
    }
  },
  beforeUpdate() {
    this.focused = document.activeElement;
  },
  updated() {
    this.focused.focus();
  },
  mounted() {
    // isRoy, category -> roy count
    this.$store.commit("addEventHandler", {
      paths: ["participants", "*", ["isRoy", "categoryId"]],
      id: 'e868f678-25fc-4de9-8199-8831de314e37',
      action: function() {
        Object.keys(this.$store.state.categories).forEach(categoryId => {
          var validationPaths = ["roy", categoryId];
          var count = Object.keys(this.$store.getters.roy[categoryId]).length;
          // invalid
          var isValid = this.$store.getters.isValidAt(validationPaths);
          if (count != 0 && count != 3) {
            if (isValid) {
              this.$store.commit("addValidationError", {
                paths: validationPaths,
                message: "Ranger roku musí být po 3 lidech"
              });
            }
          }
          // valid
          else if (!isValid) {
            this.$store.commit("removeValidationError", {
              paths: validationPaths
            });
          }
        });
      }.bind(this)
    });
    // birthDate -> category
    this.$store.commit("addEventHandler", {
      paths: ["participants", "*", "birthDate"],
      id: '082c8602-53f1-479c-ba50-fdc3e3db2dae',
      action: function(type, paths, newValue) {
        if (type != "update") return;

        const birthDate = paths.length == 3 ? newValue : newValue.birthDate;
        this.$store.dispatch("setParticipantCategory", {
          id: paths[1],
          birthDate: datetime.dateToString(birthDate)
        });
      }.bind(this)
    });

    // servant -> partilyDays
    this.$store.commit("addEventHandler", {
      paths: ["participants", "*", "isServant"],
      id: '073746ee-ed6f-44b6-9b39-5af595475989',
      action: function(type, paths) {
        if (type != "update") return;

        this.$store.commit("update", {
          paths: ["participants", paths[1], "partilyDays"],
          value: Math.pow(2, this.$store.state.partilyDays.length) - 1
        });
      }.bind(this)
    });

    // team -> isConfirmed
    this.$store.commit("addEventHandler", {
      paths: ["participants", "*", "teamId"],
      id: '859c8fbe-7c89-46c5-b811-67e7962ddae9',
      action: function(type, paths, newValue) {
        if (type != "update") return;

        // remove team
        if (newValue == null) {
          this.$store.commit("update", {
            paths: ["participants", paths[1], "isTeamConfirmed"],
            value: false
          });
          return;
        }

        // my team
        if (
          this.teams[newValue].outpostId ==
          this.$store.state.participants[paths[1]].playingOutpostId
        ) {
          this.$store.commit("update", {
            paths: ["participants", paths[1], "isTeamConfirmed"],
            value: true
          });
        }
      }.bind(this)
    });

    // playingTeam -> roy
    this.$store.commit("addEventHandler", {
      paths: ["participants", "*", "playingOutpostId"],
      id: '95e2ed03-3c3e-41bf-8642-d49fdcdae94e',
      action: function(type, paths) {
        if (type != "update") return;

        this.$store.commit("update", {
          paths: ["participants", paths[1], "isRoy"],
          value: false
        });
        this.$store.commit("update", {
          paths: ["participants", paths[1], "teamId"],
          value: null
        });
      }.bind(this)
    });

    // partilyDays -> price
    this.$store.commit("addEventHandler", {
      paths: ["participants", "*", "partilyDays"],
      id: '00f0cb94-dd26-4ed6-b69d-809959fb27e3',
      action: function(type, paths, newValue, oldValue) {
        if (type != "update") return;

        const participant = this.$store.state.participants[paths[1]];
        for (let i = 0; i < this.$store.state.partilyDays.length; i++) {
          const bit = Math.pow(2, i);
          const diff = (newValue & bit) - (oldValue & bit);
          const currentAge = this.$store.getters.currentAge;

          // add
          if (diff > 0) {
            console.log('add');
            // this.$store.commit("update", {
            //   paths: ["participants", paths[1], "createdInAgeId"],
            //   value: currentAge.id
            // });
          }
          // remove
          else if (
            diff < 0 &&
            currentAge.dayPriceDestroyed > 0
          ) {
            console.log('remove');
            this.$store.commit("add", {
              paths: ["priceModifiers", data.newGuid()],
              value: {
                message: `Odhlášení jednoho dne účastníka ${participant.name} ${participant.surname} v době ${currentAge.name}`,
                amount: currentAge.dayPriceDestroyed,
                count: 1
              }
            });
          }
        }
      }.bind(this)
    });

    // healthProblems -> none vs others
    this.$store.commit("addEventHandler", {
      paths: ["participants", "*", "healthProblems"],
      id: 'bc09091b-4925-40ae-b605-37b5ac67ec10',
      action: function(type, paths, newValue, oldValue) {
        if (type != "update") return;

        const participant = this.$store.state.participants[paths[1]];

        // clear all
        if (newValue == 0) {
          return;
        }

        // add none
        if (newValue - oldValue == 1) {
          this.$store.commit("update", {
            paths: ["participants", paths[1], "healthProblems"],
            value: 1
          });
          return;
        }

        // has none && add other
        if (newValue % 2 == 1 && newValue - oldValue > 0) {
          this.$store.commit("update", {
            paths: ["participants", paths[1], "healthProblems"],
            value: newValue - 1
          });
          return;
        }
      }.bind(this)
    });
  }
};
</script>

<style lang="sass">
@import ../sass/_components

.page_participants
  @include table-view()
  grid-template-areas: "head actions end link" "table table table table"
  grid-template-columns: auto auto 1fr
  grid-column-gap: 1rem
  h1
    grid-area: head
  .actions
    grid-area: actions
    .add
      @include first-input()
    .importInfo
      @include button-secondary()
      @include middle-input()
      white-space: nowrap
    .export
      @include button-secondary()
      @include last-input()
  .registration-end
    grid-area: end
    align-self: center
    strong
      font-weight: bold
  .exchange
    grid-area: link
    align-self: center
    justify-self: end
    @include button-secondary()
  table
    grid-area: table
    tr.disabled
      background-color: $bc-secondary
    td[data-columnName=servant] div
      display: inline-flex
    td[data-columnName=actions]
      display: flex
      .inputer-select .label
        @include first-input()
      .remove
        @include button-danger()
        @include last-input()
</style>
